import React, { useEffect, useState } from 'react';
import TopBar from '../../components/TopBar';
import { fetchAllAuctions, listBranches, createAuction, updateAuction, deleteAuction } from '../../lib/api';
import { toast } from 'react-toastify';
import { 
  FiShoppingCart, 
  FiCalendar, 
  FiMapPin, 
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiX
} from 'react-icons/fi';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import { useAuth } from '../../contexts/AuthContext';

const Auctions = () => {
  const [auctions, setAuctions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingAuction, setEditingAuction] = useState(null);
  const { branchId, isAdmin } = useBranchFilter();
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    branch_id: branchId || '',
    auction_date: ''
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const [auctionsData, branchesData] = await Promise.all([
        fetchAllAuctions(),
        listBranches()
      ]);
      
      // Filter auctions by branch for branch managers
      let filteredAuctions = auctionsData;
      if (branchId) {
        filteredAuctions = auctionsData.filter(a => a.branch_id === branchId);
      }
      
      setAuctions(filteredAuctions);
      setBranches(branchesData);
    } catch (error) {
      console.error('Failed to load data:', error);
      toast.error('Failed to load auctions');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleOpenModal = (auction = null) => {
    if (auction) {
      setEditingAuction(auction);
      setFormData({
        branch_id: auction.branch_id || '',
        auction_date: auction.auction_date ? auction.auction_date.split('T')[0] : ''
      });
    } else {
      setEditingAuction(null);
      setFormData({
        branch_id: branchId || '', // Pre-fill with user's branch if branch manager
        auction_date: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingAuction(null);
    setFormData({
      branch_id: '',
      auction_date: ''
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.branch_id || !formData.auction_date) {
      toast.error('Please fill in all fields');
      return;
    }

    try {
      if (editingAuction) {
        await updateAuction(editingAuction.id, formData);
        toast.success('Auction updated successfully');
      } else {
        await createAuction(formData);
        toast.success('Auction created successfully');
      }
      handleCloseModal();
      loadData();
    } catch (error) {
      console.error('Failed to save auction:', error);
      toast.error(`Failed to ${editingAuction ? 'update' : 'create'} auction`);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this auction?')) {
      return;
    }

    try {
      await deleteAuction(id);
      toast.success('Auction deleted successfully');
      loadData();
    } catch (error) {
      console.error('Failed to delete auction:', error);
      toast.error('Failed to delete auction');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <TopBar />
        <div className="p-6 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar />
      <div className="p-6 pb-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">Auctions</h1>
              <p className="text-gray-600 mt-1">Manage all auction events</p>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiPlus />
              <span>Add New Auction</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 mr-4">
                <FiShoppingCart className="text-red-600 text-2xl" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Auctions</p>
                <p className="text-2xl font-bold text-gray-800">{auctions.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 mr-4">
                <FiCalendar className="text-blue-600 text-2xl" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Upcoming</p>
                <p className="text-2xl font-bold text-gray-800">
                  {auctions.filter(a => new Date(a.auction_date) > new Date()).length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 mr-4">
                <FiMapPin className="text-green-600 text-2xl" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Branches</p>
                <p className="text-2xl font-bold text-gray-800">{branches.length}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Auctions Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auction ID
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Branch
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Auction Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created At
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {auctions.map((auction) => {
                  const isUpcoming = new Date(auction.auction_date) > new Date();
                  return (
                    <tr key={auction.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {auction.id ? (typeof auction.id === 'string' ? `${auction.id.substring(0, 8)}...` : auction.id) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <FiMapPin className="mr-2 text-gray-400" />
                          {auction.branch?.name || auction.branch?.branch_name || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-900">
                          <FiCalendar className="mr-2 text-gray-400" />
                          {auction.auction_date ? new Date(auction.auction_date).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          }) : 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                        {auction.created_at ? new Date(auction.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        }) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {isUpcoming ? (
                          <span className="px-3 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                            Upcoming
                          </span>
                        ) : (
                          <span className="px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
                            Completed
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-3">
                          <button
                            onClick={() => handleOpenModal(auction)}
                            className="text-blue-600 hover:text-blue-900 transition-colors"
                            title="Edit"
                          >
                            <FiEdit2 className="w-5 h-5" />
                          </button>
                          <button
                            onClick={() => handleDelete(auction.id)}
                            className="text-red-600 hover:text-red-900 transition-colors"
                            title="Delete"
                          >
                            <FiTrash2 className="w-5 h-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {auctions.length === 0 && (
                  <tr>
                    <td className="px-6 py-12 text-center text-gray-500" colSpan="6">
                      <FiShoppingCart className="mx-auto text-4xl text-gray-300 mb-2" />
                      <p className="text-lg font-medium">No auctions available</p>
                      <p className="text-sm">Create your first auction to get started</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          {auctions.length > 0 && (
            <div className="bg-gray-50 px-6 py-3 border-t border-gray-200">
              <p className="text-sm text-gray-600">
                Showing {auctions.length} auction{auctions.length !== 1 ? 's' : ''}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-gray-800">
                {editingAuction ? 'Edit Auction' : 'Add New Auction'}
              </h2>
              <button
                onClick={handleCloseModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                {/* Branch Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Branch *
                  </label>
                  <select
                    value={formData.branch_id}
                    onChange={(e) => setFormData({ ...formData, branch_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select a branch</option>
                    {branches.map((branch) => (
                      <option key={branch.id} value={branch.id}>
                        {branch.name || branch.branch_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Auction Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Auction Date *
                  </label>
                  <input
                    type="date"
                    value={formData.auction_date}
                    onChange={(e) => setFormData({ ...formData, auction_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingAuction ? 'Update' : 'Create'} Auction
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Auctions;

